import { Position, useReactFlow } from '@xyflow/react';
import { useState, useEffect } from 'react';
import { Trash } from 'react-bootstrap-icons';
import { useTrashNode } from './commons/trashNode';
import { FlowHandle } from './commons/flowHandle';
import './style.scss';
import { workflowPromptType } from 'services/dropdown-enums';
import { UilTrashAlt } from '@iconscout/react-unicons';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { useEdges } from '@xyflow/react';

export default function CustomConversationNode({ id, data, editable = true }) {
  const { setNodes, setEdges } = useReactFlow();
  const trashNode = useTrashNode();
  const edges = useEdges();

  const [text, setText] = useState(data?.text || '');

  const [promptType, setPromptType] = useState(
    data?.promptType || workflowPromptType.Prompt
  );

  // Ensure at least one transition
  // const initialTransitions =
  //   data?.transitions && data.transitions.length > 0
  //     ? data.transitions
  //     : [{ id: `transition-${id}-0`, value: "" }];

  // const [items, setItems] = useState(
  //   initialTransitions.map((t, i) => ({
  //     id: t.id || `transition-${id}-${i}`,
  //     value: t.value || "",
  //   }))
  // );

  const [items, setItems] = useState(
    data.transitions || [{ id: `transition-${id}-0`, value: '' }]
  );

  const [userOptions, setUserOptions] = useState(
    data.userOptions || [{ id: `userOption-${id}-0`, value: '' }]
  );

  // 🔹 Sync node data
  const onChange = newData => {
    setNodes(nds =>
      nds.map(node =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  // 🔹 Add transition
  const addItem = () => {
    setItems(prev => {
      const maxIndex = prev.reduce((max, item) => {
        const match = item.id.match(new RegExp(`^transition-${id}-(\\d+)$`));
        if (!match) return max;
        const num = parseInt(match[1], 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, -1);

      const nextIndex = maxIndex + 1;

      const next = [
        ...prev,
        { id: `transition-${id}-${nextIndex}`, value: '' }
      ];
      onChange({ text, transitions: next, userOptions, promptType });
      return next;
    });
  };

  // 🔹 Update transition
  const updateItem = (index, value) => {
    setItems(prev => {
      const next = prev.map((item, i) =>
        i === index ? { ...item, value } : item
      );
      onChange({ text, transitions: next, userOptions, promptType });
      return next;
    });
  };

  // 🔹 Remove transition
  const removeItem = index => {
    setItems(prev => {
      const deleted = prev[index];

      const next = prev.filter((_, i) => i !== index);

      // // Reindex but DO NOT add/remove edges for others
      // const reindexed = next.map((item, i) => ({
      //   ...item,
      //   id: `transition-${id}-${i}`
      // }));

      setEdges(eds =>
        eds.filter(
          e => e.sourceHandle !== deleted.id && e.targetHandle !== deleted.id
        )
      );

      onChange({ text, transitions: next, userOptions, promptType });

      return next;
    });
  };

  // 🔹 Add user option
  const addUserOption = () => {
    setUserOptions(prev => {
      const maxIndex = prev.reduce((max, item) => {
        const match = item.id.match(new RegExp(`^userOption-${id}-(\\d+)$`));
        if (!match) return max;
        const num = parseInt(match[1], 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, -1);

      const nextIndex = maxIndex + 1;

      const next = [
        ...prev,
        { id: `userOption-${id}-${nextIndex}`, value: '' }
      ];
      onChange({ text, transitions: items, userOptions: next, promptType });
      return next;
    });
  };

  // 🔹 Update user option
  const updateUserOption = (index, value) => {
    setUserOptions(prev => {
      const next = prev.map((item, i) =>
        i === index ? { ...item, value } : item
      );
      onChange({ text, transitions: items, userOptions: next, promptType });
      return next;
    });
  };

  // 🔹 Remove user option
  const removeUserOption = index => {
    setUserOptions(prev => {
      const deleted = prev[index];

      const next = prev.filter((_, i) => i !== index);

      // // Reindex remaining items
      // const reindexed = next.map((item, i) => ({
      //   ...item,
      //   id: `userOption-${id}-${i}`
      // }));

      // setEdges(eds =>
      //   eds.filter(
      //     e => e.sourceHandle !== deleted.id && e.targetHandle !== deleted.id
      //   )
      // );

      onChange({
        text,
        transitions: items,
        userOptions: next,
        promptType
      });

      return next;
    });
  };

  const IsValidConnection = connection => {
    // limit 1 connection per source handle
    if (edges.some(edge => edge.sourceHandle === connection.sourceHandle))
      return false;

    return true;
  };

  // 🔹 Persist transitions on first mount
  useEffect(() => {
    if (!data?.transitions || data.transitions.length === 0) {
      onChange({ text, transitions: items, userOptions, promptType });
    }
    if (!data?.userOptions || data.userOptions.length === 0) {
      onChange({ text, transitions: items, userOptions, promptType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <dl className="mt-3" style={{ width: '290px' }}>
      {/* Left (incoming) handle */}
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />
      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div className="d-flex align-items-center px-4 conversation-header">
          <div className="d-flex mr-auto ml-2">
            <dt className="text-truncate fw-semibold fs-6">Conversation</dt>
          </div>
          <div className="ms-auto">
            <button
              onClick={e => trashNode(e, id)}
              className="d-flex align-items-center rounded-circle p-2 delete-icon"
              title="Delete conversation"
              disabled={!editable}
            >
              <UilTrashAlt
                style={{ width: '24px', height: '24px', color: '#dc3545' }}
              />
              {/* <Trash size={20} color="#dc3545" /> */}
              {/* <TrashIcon style={{ width: "20px", height: "20px" }} /> */}
            </button>
          </div>
        </div>

        <div className="card-body p-2">
          {/* Conversation type */}
          <div>
            <fieldset className="mb-3">
              <div className="d-inline-flex gap-1 p-1" style={{}}>
                <label
                  className={`form-check-label px-3 py-1 rounded-22 
                  toggle-option ${
                    promptType == workflowPromptType.Prompt ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="conversationType"
                    value={workflowPromptType.Prompt}
                    checked={promptType == workflowPromptType.Prompt}
                    onChange={() => {
                      setPromptType(workflowPromptType.Prompt);
                      onChange({ promptType: workflowPromptType.Prompt });
                    }}
                    className="form-check-input d-none"
                    disabled={!editable}
                  />
                  Prompt
                </label>
                <label
                  className={`form-check-label px-3 py-1 rounded-22 
                  toggle-option ${
                    promptType == workflowPromptType.SayExactly
                      ? 'selected'
                      : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="conversationType"
                    value={workflowPromptType.SayExactly}
                    checked={promptType == workflowPromptType.SayExactly}
                    onChange={() => {
                      setPromptType(workflowPromptType.SayExactly);
                      onChange({ promptType: workflowPromptType.SayExactly });
                    }}
                    className="form-check-input d-none"
                    disabled={!editable}
                  />
                  Say Exactly
                </label>
              </div>
            </fieldset>
          </div>

          {/* Textarea */}
          <div className="mb-3 position-relative">
            <SimpleLabelTextBox
              // heading={'Prompt'}
              dataProperty={text}
              maxRows={10}
              minRows={2}
              multiline
              editable={editable}
              id={`text-${id}`}
              // error={data && !promptText && 'Should not be empty'}
              handleChange={e => {
                setText(e.target.value);
                onChange({ text: e.target.value });
              }}
              inputClasses="custom-scroll"
              placeholder={
                promptType === workflowPromptType.Prompt
                  ? 'Write a scenario'
                  : 'Write a conversation'
              }
              inputStyles={{ backgroundColor: 'white', padding: '10px' }}
              inputPropClasses="custom-placeholder"
            />
            {/* Default flow handle from text box */}
            <FlowHandle
              type="source"
              position={Position.Right}
              id={`transition-${id}-0-default`}
              style={{
                right: '-10px',
                top: '50%'
                // transform: "translateY(-50%)",
                // background: "#0d6efd", // Indigo tone for distinction
                // width: "8px",
                // height: "8px",
              }}
              isConnectable={true}
              editable={editable}
              isValidConnection={IsValidConnection}
            />
          </div>
          {/* User Options */}
          <div className="px-4 pb-3" style={{ marginBottom: '10px' }}>
            {userOptions.length > 0 && (
              <div>
                <dt
                  className="text-truncate small fw-medium mt-3 px-4"
                  style={{
                    color: '#6B7280',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  Quick Replies
                </dt>
                <div>
                  {userOptions != null &&
                    userOptions?.map((item, index) => (
                      <div
                        key={item.id}
                        className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
                      >
                        {/* Handle (must match userOption ID) */}
                        <SimpleLabelTextBox
                          dataProperty={item.value}
                          editable={editable}
                          handleChange={e =>
                            updateUserOption(index, e.target.value)
                          }
                          placeholder="User Option"
                          inputStyles={{ backgroundColor: 'white' }}
                          inputPropStyle={{ padding: '10px' }}
                          styles={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger ms-2 p-0 ml-1"
                          onClick={() => removeUserOption(index)}
                          title="Remove"
                          disabled={!editable}
                        >
                          <UilTrashAlt
                            style={{
                              width: '20px',
                              height: '20px',
                              color: '#dc3545'
                            }}
                          />
                          {/* <Trash size={14} /> */}
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={addUserOption}
              className="mt-3 fw-semibold align-middle border-0 rounded-1 shadow-sm condition-button p-1"
              disabled={!editable}
            >
              + Add Quick Replies
            </button>
          </div>
          {/* Transitions */}
          <div className="px-4 pb-3" style={{ marginBottom: '10px' }}>
            {items.length > 0 && (
              <div>
                <dt
                  className="text-truncate small fw-medium mt-3 px-4"
                  style={{
                    color: '#6B7280',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  Transition
                </dt>
                <div>
                  {items != null &&
                    items?.map((item, index) => (
                      <div
                        key={item.id}
                        className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
                      >
                        {/* Handle (must match transition ID) */}
                        <FlowHandle
                          type="source"
                          position={Position.Right}
                          id={item.id}
                          style={{
                            right: '-13px',
                            top: '50%'
                            // transform: "translateY(-50%)",
                            // background: "#0d6efd",
                            // width: "8px",
                            // height: "8px",
                          }}
                          editable={editable}
                        />
                        <SimpleLabelTextBox
                          dataProperty={item.value}
                          editable={editable}
                          handleChange={e => updateItem(index, e.target.value)}
                          placeholder="Transition condition"
                          inputStyles={{ backgroundColor: 'white' }}
                          inputPropStyle={{ padding: '10px' }}
                          styles={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger ms-2 p-0 ml-1"
                          onClick={() => removeItem(index)}
                          title="Remove"
                          disabled={!editable}
                        >
                          <UilTrashAlt
                            style={{
                              width: '20px',
                              height: '20px',
                              color: '#dc3545'
                            }}
                          />
                          {/* <Trash size={14} /> */}
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={addItem}
              className="mt-3 fw-semibold align-middle border-0 rounded-1 shadow-sm condition-button p-1"
              disabled={!editable}
            >
              + Add Condition
            </button>
          </div>
        </div>
      </div>
    </dl>
  );
}
